import React, { useState } from "react";
import HighchartsReact from "highcharts-react-official";
import { Col, Row } from "react-bootstrap";
import Highcharts from "highcharts";
import { useGetPackingSummaryQuery } from "../packing/packingApi";
import useTranslator from "../../../hooks/useTranslator";

const PackingCharts = () => {
  const [packingCheeksFilter, setPackingCheeksFilter] = useState("monthly");
  const [packingStripsFilter, setPackingStripsFilter] = useState("monthly");
  const { _t } = useTranslator();

  const filterMap = {
    Today: "hourly",
    Weekly: "weekly",
    Monthly: "monthly",
  };

  const { data: cheeksChart } = useGetPackingSummaryQuery({
    tab: "packing_cheeks_overview",
    filter: packingCheeksFilter,
  });

  const { data: stripChart } = useGetPackingSummaryQuery({
    tab: "packing_strips_overview",
    filter: packingStripsFilter,
  });

  const getDynamicChartData = (chartData) => {
    if (!chartData?.data) return [];

    const total =
      Object.values(chartData.data).reduce(
        (sum, val) => sum + Number(val),
        0
      ) || 1;

    const categoryMapping = [
      { key: "prime", name: _t("first_kg", "First KG"), color: "#46C389" },
      { key: "seconds", name: _t("second_kg", "Second KG"), color: "#EE7225" },
      { key: "humidity", name: _t("wet_kg", "Wet KG"), color: "#EE9C1F" },
      { key: "green", name: _t("green_kg", "Green KG"), color: "#EB884A" },
      { key: "bone_seed", name: _t("bone_kg", "Bone KG"), color: "#3B8B66" },
      { key: "trash", name: _t("trash_kg", "Trash KG"), color: "#FE6565" },
      { key: "toasted", name: _t("toasted", "Toasted"), color: "#FFEED4" },
      { key: "strange_material", name: _t("strange_material", "Strange Material"), color: "#54C9C9" },
      { key: "spot", name: _t("spot_kg", "Spot KG"), color: "#E2AB56" }
    ];

    const dataPoints = categoryMapping
      .map(({ key, name, color }) => ({
        name,
        y: ((Number(chartData.data[key]) || 0) / total) * 100,
        color,
      }))
      .filter((item) => item.y > 0);

    return dataPoints.length > 0
      ? dataPoints
      : [{ name: _t("no_data", "No Data"), y: 100, color: "#E8E8E8" }];
  };

  const createChartOptions = (chartData) => ({
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "bottom",
      itemMarginBottom: 5,
      itemStyle: {
        color: "#8C97A7",
        fontSize: "14px",
      },
    },
    series: [
      {
        name: _t("data", "Data"),
        colorByPoint: true,
        data: getDynamicChartData(chartData),
      },
    ],
  });

  const options = createChartOptions(cheeksChart);
  const optionsStrips = createChartOptions(stripChart);

  return (
    <Row className="mb-3">
      <Col lg={6}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">{_t("packing_cheeks_overview", "Packing/Cheeks Overview")}</h6>
            <ul className="chart_filter">
              {["Today", "Weekly", "Monthly"].map((item, index) => (
                <li
                  key={index}
                  onClick={() => setPackingCheeksFilter(filterMap[item])}
                  className={filterMap[item] === packingCheeksFilter ? "active" : ""}
                >
                  {_t(item)}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
        </div>
      </Col>
      <Col lg={6}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">{_t("packingStrips_overview", "Packing/Strips Overview")}</h6>
            <ul className="chart_filter">
              {["Today", "Weekly", "Monthly"].map((item, index) => (
                <li
                  key={index}
                  onClick={() => setPackingStripsFilter(filterMap[item])}
                  className={filterMap[item] === packingStripsFilter ? "active" : ""}
                >
                  {_t(item)}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 position-relative">
            <HighchartsReact highcharts={Highcharts} options={optionsStrips} />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default PackingCharts;
